<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ t('general.contacts') }}
        </h6>

        <CreateButton
            :label="t('address_book.create_client')"
            :to="{ name: 'addressBookClientForm' }"/>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ t('address_book.client') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :federalStatesDropdown="federalStatesDropdown"
            type="client"
            @search="getClients"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getClients"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!clients?.data?.length"
            icon="fa-address-book"
            title="Keine Kontakte gefunden"
            subtitle="Es wurden keine Kontakte gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click.prevent="sortBy('firstName')">
                  <span>{{ t('general.first_name') }}</span>
                  <i :class="getIconClass('firstName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('lastName')">
                  <span>{{ t('general.last_name') }}</span>
                  <i :class="getIconClass('lastName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('company')">
                  <span>{{ t('general.company_client') }}</span>
                  <i :class="getIconClass('company')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('phoneNumber')">
                  {{ t('general.phone_number') }}
                  <i :class="getIconClass('phoneNumber')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('email')">
                  {{ t('general.email') }}
                  <i :class="getIconClass('email')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('address')">
                  {{ t('general.street') }}
                  <i :class="getIconClass('address')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('zip')">
                  {{ t('general.postal_code') }}
                  <i :class="getIconClass('zip')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('city')">
                  {{ t('general.city') }}
                  <i :class="getIconClass('city')"></i>
                </a>
              </th>
              <th>
                <a href="#"  @click="sortBy('federalState')">
                  {{ t('general.federal_state') }}
                  <i :class="getIconClass('federalState')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('position')">
                  {{ t('general.position') }}
                  <i :class="getIconClass('position')"></i>
                </a>
              </th>
              <th>
                {{ t('general.info') }}
              </th>
              <th style="min-width: 130px!important; text-align: center">
                {{ t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(client) in clients?.data" :key="client.id">
              <td>{{ client?.firstName }}</td>
              <td>{{ client?.lastName }}</td>
              <td>{{ client?.company }}</td>
              <td>{{ client?.phoneNumber }}</td>
              <td>{{ client?.email }}</td>
              <td>{{ client?.address }}</td>
              <td>{{ client?.zip }}</td>
              <td>{{ client?.city }}</td>
              <td>{{ client?.federalStateTranslation }}</td>
              <td>{{ client?.position }}</td>
              <td>{{ client?.info ? 'JA' : 'NEIN' }}</td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({name: 'addressBookClientForm', params: {id: client?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2"
                        @click="deleteModal(client.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="clients?.pagination?.total_pages ?? 0 > 1"
            :totalPages="clients?.pagination?.total_pages"
            :currentPage="clients?.pagination?.current_page"
            :totalItems="clients?.pagination?.total_items"
            :perPage="clients?.pagination?.items_per_page"
            @pagechanged="getClients"/>
      </div>
    </div>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDeleteClient">
    </ModalDelete>

  </PageTemplate>
</template>

<script setup lang="ts">
import clientService from '@/api/AddressBook/client';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import Search from '../Search.vue';
import {IAddressBookFilters, IAddressBookList} from "@/interfaces/AddressBook/IAddressBook";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const clients = ref<IAddressBookList>();
const federalStatesDropdown = ref([]);
const clientId = ref<number | null>(null);
const showModal = ref(false);
const isSearching = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

const filters = ref<IAddressBookFilters>({
  firstName: '',
  lastName: '',
  city: '',
  company: '',
  phoneNumber: '',
  position: '',
  email: '',
  federalState: '',
  orderBy: 'DESC',
  sortBy: '',
  page: 1,
  limit: 10,
});

async function getClients(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IAddressBookFilters
) {

  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = clients.value?.pagination?.items_per_page;

  try {
    const {data} = await clientService.getClients({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    clients.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function confirmDeleteClient() {
  try {
    await clientService.deleteClient(clientId.value!);
    clientId.value = null;
    showModal.value = false;
    await getClients();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

async function sortBy(key: keyof IAddressBookFilters) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  await getClients();
}

async function getFederalStateDropdown() {
  federalStatesDropdown.value = getEnums.value.federalStates;
}

function deleteModal(id: number) {
  CloseToast();
  clientId.value = id;
  showModal.value = true;
}

async function getExcelExport() {
  await downloadFile('/address-book/client/excel-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  CloseToast()
  await Promise.all([
    getClients(),
    getFederalStateDropdown(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>